import { FormikProps, useFormikContext } from 'formik';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlusCircle } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { setHsCodeLookupResults } from '../../../../../actions/customsActions';
import { IFormSelectOption } from '../../../../../interfaces/forms/forms';
import AnimatedHeight from '../../../../base/animatedHeight';
import HsCodeLookup, { IHsCodeSelection } from '../hsCodeLookup/hsCodeLookup';
import { IHsCodeLookupFormValues } from '../hsCodeLookup/hsCodeLookup.schema';
import { IStepExportFormInfo } from '../stepExport.schema';
import { DOCUMENTS_HS_CODE } from '../stepExportForm/helpers';
import { SectionHeading } from '../stepExportForm/stepExportForm.styles';
import CustomsItem from './customsItem';

interface Props {
	forcedDocuments: boolean;
	isUSAorPR: boolean;
	shouldHidePrice: boolean;
	showWeight: boolean;
	recipientCountry: string;
	countries: IFormSelectOption[];
}

const emptyContentLine = {
	amountInPackage: '' as const,
	unitPrice: '',
	price: '',
	weight: '',
	contents: '',
	countryOfOrigin: null,
	hsTariffCode: '',
	hsChapterTitle: '',
	hsHeadingTitle: '',
	hsManualEntry: false,
};

// Gap between items. The insert zones are exactly this tall and replace the gap, so nothing moves when they hide.
const ITEM_GAP = '2.4rem';
const LAST_ITEM_GAP = '1.6rem';

/**
 * Orchestrates the customs items list and the HS code lookup. A single `hsCodeLookupIndex`
 * decides what the lookup replaces:
 *   -1                       -> no lookup; every item shows as a card
 *   === contents.length      -> lookup shown at the bottom (adding a new item)
 *   any other index          -> lookup shown in place of that item's card (editing its code)
 * `lookupInsertIndex` instead opens a lookup between two items, inserting the new item there.
 */
const CustomsInformation: FC<Props> = ({
	forcedDocuments,
	isUSAorPR,
	shouldHidePrice,
	showWeight,
	recipientCountry,
	countries,
}) => {
	const { t } = useTranslation('registerShipment');
	const dispatch = useDispatch<any>();
	const { values, touched, setFieldValue, setTouched, submitCount } = useFormikContext<IStepExportFormInfo>();

	// Open the lookup initially if there is no real (user-selected) HS code yet and this isn't a
	// forced-documents shipment. The hardcoded documents code doesn't count as a real selection —
	// when toggling away from documents the component remounts before contents has been cleared, so
	// treat that code as empty here to make sure the lookup reopens.
	const firstCode = values.contents[0]?.hsTariffCode;
	const hasRealFirstCode = !!firstCode && firstCode !== DOCUMENTS_HS_CODE;
	const [hsCodeLookupIndex, setHsCodeLookupIndex] = useState<number>(
		forcedDocuments || hasRealFirstCode ? -1 : 0
	);
	const [lookupInsertIndex, setLookupInsertIndex] = useState<number | null>(null);

	// Ref to the open lookup's own Formik. The lookup runs a separate Formik, so the outer
	// "Staðfesta" submit can't validate it directly — when the outer form is submitted while a
	// lookup is open, trigger the lookup's validation so its required error (name) shows.
	const lookupFormikRef = useRef<FormikProps<IHsCodeLookupFormValues>>(null);
	const lookupOpen = !forcedDocuments && (hsCodeLookupIndex !== -1 || lookupInsertIndex !== null);
	const showAddControls = !forcedDocuments && hsCodeLookupIndex === -1 && lookupInsertIndex === null;

	useEffect(() => {
		if (submitCount > 0 && lookupOpen) {
			lookupFormikRef.current?.submitForm();
		}
	}, [submitCount, lookupOpen]);

	// How many items have a code selected. Counted per line rather than per distinct code,
	// because the same HS code may legitimately be used on several items.
	const selectedItemCount = useMemo(
		() => values.contents.filter(line => !!line.hsTariffCode).length,
		[values.contents]
	);

	const closeLookup = () => {
		setHsCodeLookupIndex(-1);
		setLookupInsertIndex(null);
		dispatch(setHsCodeLookupResults(null));
	};

	const handleSelect = ({ hsCode, description, hsChapter, hsHeading, manualEntry }: IHsCodeSelection) => {
		const hsCodeFields = {
			hsTariffCode: hsCode,
			contents: description.slice(0, 255),
			hsChapterTitle: hsChapter,
			hsHeadingTitle: hsHeading,
			hsManualEntry: manualEntry,
		};
		const item = {
			...emptyContentLine,
			...hsCodeFields,
			unitPrice: shouldHidePrice ? '0' : '',
			price: shouldHidePrice ? '0' : '',
		};
		const targetIndex = lookupInsertIndex ?? hsCodeLookupIndex;
		if (lookupInsertIndex !== null) {
			const next = [...values.contents];
			next.splice(lookupInsertIndex, 0, item);
			// Shift the touched state along with the items so the new item doesn't inherit it
			if (Array.isArray(touched.contents)) {
				const nextTouched = [...touched.contents];
				nextTouched.splice(lookupInsertIndex, 0, undefined);
				setTouched({ ...touched, contents: nextTouched }, false);
			}
			setFieldValue('contents', next, false);
		} else if (hsCodeLookupIndex === values.contents.length) {
			setFieldValue('contents', [...values.contents, item], false);
		} else if (hsCodeLookupIndex !== -1) {
			// Editing an existing line: keep its existing quantity/price/etc, replace HS fields + description.
			setFieldValue(
				`contents.${hsCodeLookupIndex}`,
				{ ...values.contents[hsCodeLookupIndex], ...hsCodeFields },
				false
			);
		}
		closeLookup();

		// After the item card renders, focus its first field so the user can continue filling the
		// item straight away.
		setTimeout(() => {
			document.getElementById(`contents.${targetIndex}.countryOfOrigin-input`)?.focus();
		}, 0);
	};

	const removeItem = (index: number) => {
		const next = values.contents.filter((_, i) => i !== index);
		setLookupInsertIndex(null);
		if (next.length === 0) {
			// Don't leave contents empty — an empty array passes validation and would let the user
			// confirm with no items. Reset to a single blank line (invalid → blocks confirm) and
			// reopen the lookup on it.
			setFieldValue('contents', [{ ...emptyContentLine }]);
			setHsCodeLookupIndex(0);
		} else {
			setFieldValue('contents', next);
			if (hsCodeLookupIndex !== -1) {
				setHsCodeLookupIndex(-1);
			}
		}
	};

	// Only offer cancel when there is at least one real (code-selected) item to fall back to;
	// otherwise cancelling would reveal an empty customs item.
	const cancelLookup = selectedItemCount > 0 ? closeLookup : undefined;

	return (
		<>
			<SectionHeading>{t('step-export.customsInfoHeader')}</SectionHeading>
			<Wrapper>
				{values.contents.map((line, index) => {
					// International letters show the fixed documents item as a read-only card (no lookup).
					const showLookup = hsCodeLookupIndex === index && !forcedDocuments;
					// Don't prefill from the leftover hardcoded documents line (e.g. right after toggling
					// away from Documents, before contents has been cleared).
					const isDocumentsLine = line.hsTariffCode === DOCUMENTS_HS_CODE;
					const isLast = index === values.contents.length - 1;
					const showInsertZone = showAddControls && !isLast;
					return (
						<React.Fragment key={index}>
							{lookupInsertIndex === index && (
								<Slot $marginBottom={ITEM_GAP}>
									<HsCodeLookup
										formikRef={lookupFormikRef}
										recipientCountry={recipientCountry}
										onSelect={handleSelect}
										onCancel={closeLookup}
									/>
								</Slot>
							)}
							<Slot $marginBottom={showInsertZone ? '0' : isLast ? LAST_ITEM_GAP : ITEM_GAP}>
								<AnimatedHeight animationKey={showLookup ? 'lookup' : 'item'} overflowMargin={4}>
									{showLookup ? (
										<HsCodeLookup
											key={`lookup-${index}`}
											formikRef={lookupFormikRef}
											recipientCountry={recipientCountry}
											onSelect={handleSelect}
											onCancel={cancelLookup}
											// This lookup stands in for an existing content line, so the step can't
											// be confirmed until it yields a code — after a submit attempt, say so.
											selectionRequired={submitCount > 0 && !line.hsTariffCode}
											prefilledSearchTerm={isDocumentsLine ? '' : line.contents}
											prefilledHsCode={isDocumentsLine ? '' : line.hsTariffCode}
											startInManualEntry={!isDocumentsLine && !!line.hsManualEntry}
										/>
									) : (
										<CustomsItem
											// Index-keyed rather than code-keyed: the same HS code may appear on
											// several items, and duplicate keys would make React cross-wire their
											// cards. Safe here because every field is a controlled Formik field read
											// from contents[index].
											key={`item-${index}`}
											index={index}
											forcedDocuments={forcedDocuments}
											isUSAorPR={isUSAorPR}
											countries={countries}
											shouldHidePrice={shouldHidePrice}
											showWeight={showWeight}
											openLookup={() => {
												setLookupInsertIndex(null);
												setHsCodeLookupIndex(index);
											}}
											removeItem={() => removeItem(index)}
										/>
									)}
								</AnimatedHeight>
							</Slot>
							{showInsertZone && (
								<InsertZone
									type="button"
									title={t('step-export.insert-item')}
									aria-label={t('step-export.insert-item')}
									onClick={() => setLookupInsertIndex(index + 1)}>
									<HoverLine />
									<HoverIcon>
										<FiPlusCircle />
									</HoverIcon>
									<HoverLine />
								</InsertZone>
							)}
						</React.Fragment>
					);
				})}

				{!forcedDocuments && hsCodeLookupIndex === values.contents.length && (
					<HsCodeLookup
						formikRef={lookupFormikRef}
						recipientCountry={recipientCountry}
						onSelect={handleSelect}
						onCancel={cancelLookup}
					/>
				)}

				{showAddControls && (
					<AddItemRow>
						<HoverLine />
						<AddItemButton type="button" onClick={() => setHsCodeLookupIndex(values.contents.length)}>
							<FiPlusCircle />
							{t('step-export.add-item')}
						</AddItemButton>
						<HoverLine />
					</AddItemRow>
				)}
			</Wrapper>
		</>
	);
};

export default CustomsInformation;

const Wrapper = styled.div`
	margin-bottom: 2.5rem;
`;

const Slot = styled.div<{ $marginBottom: string }>`
	margin-bottom: ${({ $marginBottom }) => $marginBottom};
`;

// Hidden pink line and plus icon shared by the add item button and the insert zones between items
const HoverLine = styled.span`
	flex: 1 1 auto;
	height: 1px;
	background-color: ${({ theme }) => theme.colorsV2.mainPink};
	opacity: 0;
	transform: scaleX(0.6);
	transition: opacity 0.15s ease, transform 0.15s ease;
	pointer-events: none;

	&:first-child {
		transform-origin: right;
	}
	&:last-child {
		transform-origin: left;
	}
`;

const HoverIcon = styled.span`
	display: flex;
	color: ${({ theme }) => theme.colorsV2.mainPink};
	opacity: 0;
	transform: scale(0.6);
	transition: opacity 0.15s ease, transform 0.15s ease;
	pointer-events: none;

	svg {
		width: 2rem;
		height: 2rem;
	}
`;

const revealHoverDecoration = css`
	${HoverLine}, ${HoverIcon} {
		opacity: 1;
		transform: none;
	}
`;

const AddItemRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.8rem;

	&:has(button:hover),
	&:has(button:focus-visible) {
		${revealHoverDecoration};
	}
`;

const AddItemButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 0.8rem;
	padding: 0.8rem 1.6rem;
	border: 1px solid ${({ theme }) => theme.colorsV2.mainPink};
	border-radius: 8px;
	background: none;
	color: ${({ theme }) => theme.colorsV2.mainPink};
	font-family: ${({ theme }) => theme.fonts.ArimoBold};
	font-size: 1.6rem;
	cursor: pointer;
	transition: background-color 0.15s, color 0.15s;

	svg {
		flex-shrink: 0;
		width: 2rem;
		height: 2rem;
	}

	&:hover,
	&:focus-visible {
		outline: none;
		background-color: ${({ theme }) => theme.colorsV2.mainPink};
		color: ${({ theme }) => theme.colors.white};
	}
`;

const InsertZone = styled.button`
	display: flex;
	align-items: center;
	gap: 0.4rem;
	width: 100%;
	height: ${ITEM_GAP};
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;

	&:hover,
	&:focus-visible {
		outline: none;
		${revealHoverDecoration};
	}
`;
